import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { api, ApiError, setCsrfToken } from '../../lib/api'
import type { User } from '../../lib/types'

type AuthContextValue = {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  register: (name: string, email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const result = await api<{ user: User; csrf_token: string }>('/me')
      setUser(result.data.user)
      setCsrfToken(result.data.csrf_token)
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 401) console.error(error)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void refresh() }, [refresh])

  const login = async (email: string, password: string) => {
    const result = await api<{ user: User; csrf_token: string }>('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) })
    setUser(result.data.user)
    setCsrfToken(result.data.csrf_token)
  }
  const register = async (name: string, email: string, password: string) => {
    const result = await api<{ user: User; csrf_token: string }>('/auth/register', { method: 'POST', body: JSON.stringify({ name, email, password }) })
    setUser(result.data.user)
    setCsrfToken(result.data.csrf_token)
  }
  const logout = async () => { await api('/auth/logout', { method: 'POST' }); setUser(null); setCsrfToken('') }

  const value = useMemo(() => ({ user, loading, login, register, logout, refresh }), [user, loading, refresh])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// This hook intentionally shares the provider module so the public API stays cohesive.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used within AuthProvider')
  return value
}
